import { useState, type CSSProperties } from 'react';
import type { VideoReference } from '@/api/types';
import { useT } from '@/i18n/LocaleProvider';
import { buildEmbedUrl, embedAspect, embedChrome } from './embed';
import { posterFor } from './HostedPlayer';
import styles from './VideoDrawer.module.css';

/**
 * Embed площадки (PLAY-1, PLAY-5): постер с пометкой «Plays from the original
 * platform», по клику монтируется iframe с autoplay — тап отдаёт управление
 * встроенному плееру (VID-5). Неизвестная площадка — только пометка.
 */
export function EmbedPlayer({ video }: { video: VideoReference }) {
  const t = useT();
  const [active, setActive] = useState(false);
  const url = buildEmbedUrl(video, true);
  const poster = posterFor(video);

  if (active && url) {
    const chrome = embedChrome(video.platform);
    // Рамка по пропорциям кадра: iframe шире не бывает, поле площадки уходит за обрез.
    const fit = { '--media-ar': embedAspect(video) } as CSSProperties;
    const frame = {
      top: `-${chrome.top}px`,
      height: `calc(100% + ${chrome.top + chrome.bottom}px)`,
    } as CSSProperties;
    return (
      <div className={styles.embedFit} style={fit}>
        <iframe
          className={styles.iframe}
          style={frame}
          src={url}
          title={video.title}
          scrolling="no"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  return (
    <>
      {poster ? <img className={styles.poster} src={poster} alt="" /> : null}
      {url ? (
        <button
          type="button"
          className={styles.overlay}
          onClick={() => setActive(true)}
          aria-label={t('video.playOriginal')}
        />
      ) : null}
      <div className={styles.embedNote}>
        <div className={styles.embedNoteBox}>
          <div className={styles.embedNoteEyebrow}>{t('video.embedEyebrow')}</div>
          <div className={styles.embedNoteText}>{t('video.embedNote')}</div>
        </div>
      </div>
    </>
  );
}
